<template>
    <div v-if="isAdminMode" class="admin-mode-bar">
      Admin: "{{ currentSchoolName }}"
    </div>
    <div class="container-fluid" :class="{ 'with-admin-bar': isAdminMode }">
      <div class="versionNumber">v{{ this.cachedAppVersion }}</div>
      <div class="row">
        <nav class="col-md-2 d-none d-md-block sidebar" v-if="showMenuSidebar">
          <div class="portrait-items">
            <img src="/assets/images/yuha-logo.svg" class="portrait-logo" alt="Yuha" />
            <div class="hamburger" @click="toggleMenu">
              <fa :icon="faBars" />
            </div>
          </div>
          <div class="sidebar-sticky">
            <ul class="nav flex-column" :class="{ 'show': this.$store.state.showMenu }">
              <div class="yuha-brand img-fluid" :class="{ 'with-admin-bar': isAdminMode }">
                <router-link to="/dashboard"><img src="/assets/images/yuha-logo.svg" class="img-fluid" alt="Yuha" /></router-link>
              </div>

              <router-link to="/settings">
                <div class="avatar d-flex justify-content-center rounded-circle" v-bind:style="[(this.$store.state.user && this.$store.state.user.avatar) ? { backgroundImage: 'url(' + this.$store.state.user.avatar + ')' } : {} ]"></div>
             </router-link>

              <li class="nav-item" v-for="item in visibleNavigationItems" :key="item.to">
                <router-link class="nav-link" :to="item.to" :class="{ 'active': $route.path === item.to }">
                  <fa :icon="item.icon" /> {{ item.label }}
                </router-link>
              </li>
              <li class="nav-item">
                <a class="nav-link" href="#" @click="logout">
                  <fa :icon="faRightFromBracket" /> Uitloggen
                </a>
              </li>
            </ul>
          </div>
        </nav>

        <main
          role="main"
          class="main-content"
          :class="[
            showMenuSidebar ? 'with-sidebar' : 'without-sidebar',
            isMultiViewRoute ? 'main-multiview' : (isFlowRoute ? 'main-flow' : 'main-padding')
          ]"
        >
          <router-view></router-view>
        </main>
      </div>
    </div>
</template>

<script>
// Refactored to modular Firestore API
import { auth, db, doc, getDoc } from './main';
import { warmUpRouteChunks } from './router';
import { getUxFlowRoute } from './flows/uxFlows';
import { colleaguesLabel, studentsLabel } from './components/Teachers/schoolUsers';
import { faBars, faBarsStaggered, faCube, faGamepad, faGear, faHome, faLocationDot, faPersonChalkboard, faRightFromBracket, faSchool, faTrophy, faUserGroup, faVrCardboard, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
export default {
  name: 'App',
  computed: {
    showMenuSidebar() {
      // Only for a signed-in user of the standard portal, once the router knows the real route
      // (before that $route is '/' and the sidebar would flash on the flow and invitation pages)
      const user = this.$store.state.user;
      if (!user || this.$route.matched.length === 0) return false;
      if (!user.admin && getUxFlowRoute(this.$store.state.uxFlow)) return false;

      //Exclude sidebar in following routes
      return this.$route.path !== '/login' &&
      (this.$route.path.indexOf('/invitation') < 0) &&
      (this.$route.path.indexOf('/activities/hidden') < 0) &&
      (this.$route.path !== '/multiview') &&
      !this.isFlowRoute
    },
    isAdminMode() {
      // Check if admin is viewing a different school than their default
      return this.$store.state.user && 
             this.$store.state.user.admin && 
             this.$store.state.schoolId && 
             this.$store.state.user.schoolId !== this.$store.state.schoolId;
    },
    currentSchoolName() {
      // Get the school name from state if available
      return this.schoolName || 'School wordt geladen...';
    },
    isFlowRoute() {
      return this.$route.path.startsWith('/flow/');
    },
    isMultiViewRoute() {
      return this.$route.path === '/multiview';
    },
    visibleNavigationItems() {
      const isAdmin = Boolean(this.$store.state.user && this.$store.state.user.admin);

      const user = this.$store.state.user || {};
      const items = this.navigationItems
        .filter((item) => !item.adminOnly || isAdmin)
        .filter((item) => !item.permission || isAdmin || user[item.permission] === true)
        // Leerkrachten/Leerlingen at a school, Begeleiders/Kinderen at a BSO
        .map((item) => item.to === '/teachers' ? { ...item, label: colleaguesLabel(this.$store.state.showBsoFeatures) } : item)
        .map((item) => item.to === '/students' ? { ...item, label: studentsLabel(this.$store.state.showBsoFeatures) } : item);

      // Admins can open the alternative UX flow of the active school to see what its users get
      const flowRoute = getUxFlowRoute(this.$store.state.uxFlow);
      if (isAdmin && flowRoute) {
        items.splice(1, 0, { to: flowRoute, label: 'Open BSO-flow', icon: faWandMagicSparkles });
      }

      return items;
    }
  },
  data(){
    return {
      faBars, faRightFromBracket,
      showMenu: false,
      cacheCheck: true,
      cachedAppVersion: 133,
      schoolName: '',
      navigationItems: [
        { to: '/dashboard', label: 'Dashboard', icon: faHome },
        { to: '/students', label: 'Leerlingen', icon: faUserGroup },
        { to: '/teachers', label: 'Leerkrachten', icon: faPersonChalkboard, permission: 'manageUsers' },
        { to: '/locations', label: 'Locaties', icon: faLocationDot, permission: 'manageLocations' },
        { to: '/activities/normal', label: 'Activiteiten', icon: faBarsStaggered },
        { to: '/leaderboards', label: 'Scorebord', icon: faTrophy },
        { to: '/devices', label: 'VR brillen', icon: faVrCardboard },
        { to: '/clients', label: 'Klanten', icon: faSchool, adminOnly: true },
        { to: '/challenges', label: 'Uitdagingen', icon: faGamepad, adminOnly: true },
        { to: '/generate', label: 'Genereren', icon: faCube, permission: 'contentCreator' },
        { to: '/settings', label: 'Instellingen', icon: faGear }
      ]
    }
  },
  watch: {
    // Watch for changes to schoolId and fetch the school name
    '$store.state.schoolId': {
      handler(newSchoolId, oldSchoolId) {
        if (newSchoolId && this.isAdminMode) {
          this.fetchSchoolName(newSchoolId);
        } else {
          this.schoolName = '';
        }

        // Re-hydrate the school feature flags when switching schools (admin).
        // The initial load is handled once in main.js before the app mounts.
        if (newSchoolId && oldSchoolId !== undefined && newSchoolId !== oldSchoolId) {
          this.hydrateSchoolSettings(newSchoolId);
        }
      },
      immediate: true
    }
  },
  mounted() 
  {
    if(!this.cacheCheck){
      return;
    }

    const routesToWarm = [
      '/dashboard',
      '/students',
      '/activities/normal',
      '/leaderboards',
      '/devices',
      '/settings',
      '/activity/_/_/_',
      '/student/_'
    ];

    if (this.$store.state.user?.admin) {
      routesToWarm.push('/teachers', '/locations', '/clients', '/challenges', '/generate');
    }

    warmUpRouteChunks(routesToWarm);
  },
  methods: {
    checkVersionCache()
    {
      // Not used right now, but can be used to check/force app refresh if the version in Firestore is different from the cached version
      getDoc(doc(db, 'settings', 'app')).then((docSnap) => {
        if (docSnap.exists()) {
          const appSettings = docSnap.data();
          console.log(`Version check. Local:${this.cachedAppVersion} == Remote:${appSettings.version}`);
          if(this.cachedAppVersion != String(appSettings.version)) {
            console.log(`Refreshing cached index.html`);
            const baseURL = window.location.href.split("?");
            const redirectUrl = baseURL[0] + "?v=" + appSettings.version;
            window.location.href = redirectUrl;
          }
        }
      });
    },
    toggleMenu() {
      this.$store.commit('SET_SHOW_MENU', !this.$store.state.showMenu);
    },
    logout(){
      auth.signOut();
    },
    async fetchSchoolName(schoolId) {
      try {
        const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
        if (schoolDoc.exists()) {
          this.schoolName = schoolDoc.data().name || '';
        }
      } catch (error) {
        console.error('Error fetching school name:', error);
        this.schoolName = 'School';
      }
    },
    async hydrateSchoolSettings(schoolId) {
      try {
        const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
        if (schoolDoc.exists()) {
          this.$store.dispatch('applySchoolSettings', schoolDoc.data());
        }
      } catch (error) {
        console.error('Error hydrating school settings:', error);
      }
    }
  }
};
</script>

<style>
/* App shell: sidebar, main content area and the admin bar. Bootstrap itself is themed in
   src/assets/scss, shared portal patterns live in src/assets/styles/portal.css. */

.versionNumber {
  font-size: 12px;
  position: fixed;
  right: 10px;
  bottom: 10px;
  color: var(--text-muted);
}

/* The workspace is a soft blue wash, like the tinted sections on yuha.io, so white panels
   and the sidebar read as surfaces on top of it. */
body {
  background-color: var(--surface-workspace);
  --sidebar-width: 220px;
  --sidebar-inset: 16px;
  --sidebar-space: calc(var(--sidebar-width) + 2 * var(--sidebar-inset));
}

.avatar {
  margin-left: auto;
  margin-right: auto;
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  border: 3px solid var(--blue-200);
  background-position: center;
  background-size: cover;
  background-color: var(--blue-200);
}

.portrait-items {
  display: none;
  justify-content: space-between;
  align-items: center;
  /* same inset as the floating sidebar on desktop (--sidebar-inset) */
  padding: 0 16px;
}

.portrait-logo {
  height: 20px;
}

.main-multiview {
  padding: 0 !important;
  height: 100vh;
  overflow: hidden;
}

.hamburger {
  color: var(--blue-200);
  border-radius: var(--radius-md);
  background-color: var(--blue-600);
  width: 40px;
  height: 40px;
  justify-content: center;
  display: flex;
  align-items: center;
  margin: 10px;
  cursor: pointer;
  transition: var(--transition-button);
}

.hamburger:hover {
  color: var(--blue-600);
  background-color: var(--blue-200);
}

/*
 * Sidebar
 */

/* A floating white panel, the portal's version of the nav capsule on yuha.io (and the
   only place that uses its shadow). */
.sidebar {
  width: var(--sidebar-width);
  max-width: var(--sidebar-width);
  min-width: var(--sidebar-width);
  position: fixed;
  top: var(--sidebar-inset);
  bottom: var(--sidebar-inset);
  left: var(--sidebar-inset);
  z-index: 100;
  padding: 0;
  background-color: var(--surface-nav);
  border-radius: var(--radius-panel);
  box-shadow: var(--shadow-nav);
  /* Larger than the site's nav size: at 220px wide the longest item still fits */
  font-size: var(--text-small);
  overflow: hidden;
}

.sidebar-sticky {
  position: sticky;
  height: 100%;
  padding-bottom: 12px;
  overflow-x: hidden;
  overflow-y: auto; /* Scrollable contents if viewport is shorter than content. */
}

/* The top space matches the panel radius, so the logo clears the rounded corner. */
.yuha-brand {
  display: flex;
  justify-content: center;
  padding: 40px 32px 32px;
}

.sidebar .nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 2px 12px;
  padding: 0.55rem 1rem;
  color: var(--blue-600);
  font-weight: 400;
  border-radius: var(--radius-pill);
  text-wrap: nowrap;
  transition: var(--transition-button);
}

.sidebar .nav-link svg {
  width: 1.2em;
  min-width: 1.2em;
  text-align: center;
  color: var(--blue-400);
}

.sidebar .nav-link:hover {
  background-color: var(--surface-tint-blue);
}

.sidebar .nav-link.active {
  background-color: var(--blue-200);
  font-weight: 700;
}

.sidebar .nav-link.active svg {
  color: var(--blue-600);
}

/* Main content responsive behavior */
.main-content {
  transition: margin-left 0.3s ease, margin-right 0.3s ease;
}

/* The padding scales with the window: a fixed 48px on both sides costs a tenth of the
   screen on a small laptop. */
.main-padding {
  padding: clamp(20px, 2.4vw, 48px) clamp(16px, 2.4vw, 48px);
}

/* Below 1200px every pixel counts, so the rail and its inset come in a little. */
@media (max-width: 1200px) {
  body {
    --sidebar-width: 190px;
    --sidebar-inset: 10px;
  }

  /* The narrower rail only has room for the site's nav size */
  .sidebar {
    font-size: var(--text-nav);
  }
}

/* Desktop behavior with sidebar. Percentages, not vw: 100vw includes the vertical
   scrollbar and made the page scroll sideways. */
@media (min-width: 768px) {
  .main-content.with-sidebar {
    max-width: calc(100% - var(--sidebar-space));
    /* Try to center with max-width, but ensure we don't go under sidebar */
    width: min(1200px, calc(100% - var(--sidebar-space)));
    margin-right: auto;
    /* Center based on the full width, but never go under sidebar */
    margin-left: max(var(--sidebar-space), calc(50% - 600px));
  }

  .main-content.without-sidebar {
    max-width: 1200px;
    width: 100%;
    margin-right: auto;
    /* Center when no sidebar */
    margin-left: max(0px, calc(50% - 600px));
  }
}

/* Mobile: the sidebar becomes a top bar with a hamburger menu */
@media (max-width: 767px) {
  .main-content.with-sidebar,
  .main-content.without-sidebar {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    max-width: 100%;
  }

  .sidebar {
    display: block !important;
    max-width: 100%;
    min-width: 100%;
    width: 100%;
    position: static;
    border-radius: 0;
    overflow: hidden;
  }
  .sidebar-sticky .nav {
    display: none;
  }
  .sidebar-sticky .nav.show {
    display: flex;
    padding-bottom: 12px;
  }
  .yuha-brand {
    display: none;
    text-align: center;
  }
  .sidebar .avatar {
    display: none;
  }
  .portrait-items {
    min-height: 56px;
    display: flex;
  }

  /* the panel padding is for the floating desktop sidebar; on the top bar it would
     push the logo and hamburger off centre */
  .sidebar-sticky {
    padding-bottom: 0;
  }

  .hamburger {
    margin: 0;
  }
}

/*
 * Admin bar, shown while an admin is viewing another school
 */

.admin-mode-bar {
  background-color: var(--pink-400);
  color: var(--pink-600);
  padding: 12px 0;
  text-align: center;
  font-weight: 700;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: var(--shadow-nav);
}

.container-fluid.with-admin-bar {
  padding-top: 48px;
}
.yuha-brand.with-admin-bar {
  margin-top: 48px;
}

/* Alternative UX flows bring their own full-screen layout, at every window width */
.main-content.without-sidebar.main-flow {
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: none;
}
</style>
